import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";

/**
 * メディア文字起こし API（AWS Transcribe）。
 *
 * - `GET    /api/media/:fileId/transcription` ステータス / 結果 URL 取得（ポーリング用）
 * - `POST   /api/media/:fileId/transcription` 文字起こしの実行（再実行で上書き）
 * - `DELETE /api/media/:fileId/transcription` 文字起こしの削除
 * - `POST   /api/media/transcription/statuses` 一覧ぶんのステータス一括取得
 * - `POST   /api/media/transcription/bulk` フォルダ / 一覧単位の一括実行
 *
 * 文字起こしは Celery + AWS Transcribe による非同期処理のため、処理中は
 * `useGetTranscriptionQuery` の `pollingInterval` で完了を検知する
 * （`MediaTranscriptionModal` を参照）。一覧のチップは
 * `useTranscriptionStatuses` が statuses をポーリングして更新する。
 *
 * fileId はバックエンドのメディア横断レジストリで解決される
 * （ショット納品 / ショットコメント添付 / トークルーム添付 / DM 添付 /
 * グループチャット添付 / 共有ライブラリ / マイサイト作品メディア）。
 * 詳細: docs/api/media-transcription.md（soremo-soundcheck）
 */

export type TranscriptionStatus =
  | "none"
  | "pending"
  | "processing"
  | "complete"
  | "failed";

/**
 * 認識エンジンの選択。
 * - `conversation`: AWS Transcribe。会話向けで、話者分離が使える（既定）
 * - `lyrics`: OpenAI Whisper。BGM 込みの歌唱に強い。話者分離は無い
 */
export type TranscriptionMode = "conversation" | "lyrics";

export interface TranscriptionData {
  file_id: string;
  source_type: string;
  transcription_status: TranscriptionStatus;
  language_code?: string | null;
  duration_seconds?: number | null;
  has_speaker_labels?: boolean;
  mode?: TranscriptionMode;
  /** 歌唱モードで使った語彙ヒント（曲名・アーティスト名など） */
  prompt_hint?: string | null;
  transcript_url?: string | null;
  error_message?: string | null;
  /**
   * 失敗の識別子（`LimitExceededException` / `MediaTooLong` / `PollTimeout` 等）。
   * 表示用の文言は `error_message` を使い、こちらは出し分けと問い合わせ時の
   * 切り分けに使う。AWS の生メッセージは含まない。
   */
  error_code?: string | null;
  requested_by_name?: string | null;
  updated_at?: string | null;
  completed_at?: string | null;
}

export interface TranscriptionResponse {
  status: string;
  data: TranscriptionData;
}

/** transcript_url が指す正規化済み JSON のセグメント */
export interface TranscriptSegment {
  id: number;
  start: number;
  end: number;
  text: string;
  speaker: string | null;
}

export interface TranscriptJson {
  language_code: string;
  duration_seconds: number;
  segments: TranscriptSegment[];
}

/** 単体 / 一括で共通の実行オプション（`TranscriptionOptionsForm` が生成する） */
export interface TranscriptionOptions {
  languageCode?: "auto" | "ja-JP" | "en-US";
  speakerLabels?: boolean;
  /** 既定は `conversation`（AWS Transcribe） */
  mode?: TranscriptionMode;
  /** 歌唱モードの語彙ヒント（曲名・アーティスト名など。200 文字まで） */
  promptHint?: string;
}

export interface StartTranscriptionArgs extends TranscriptionOptions {
  fileId: string;
}

export interface BulkTranscriptionArgs extends TranscriptionOptions {
  fileIds: string[];
}

/** 一覧のチップ / 本文プレビュー用の最小ステータス */
export interface TranscriptionStatusEntry {
  file_id: string;
  transcription_status: TranscriptionStatus;
  completed_at?: string | null;
  /**
   * complete のときの本文プレビュー（先頭 200 文字を 1 行に畳んだもの）。
   * タイムスタンプは含まれない。complete 以外は null。
   */
  text_preview?: string | null;
}

export interface TranscriptionStatusesResponse {
  status: string;
  data: { statuses: TranscriptionStatusEntry[] };
}

/** 一括実行でスキップされたファイルの内訳 */
export interface BulkTranscriptionSkipped {
  already_done: number;
  running: number;
  unsupported: number;
  too_long: number;
  no_permission: number;
}

export interface BulkTranscriptionResponse {
  status: string;
  data: {
    /** 実行を開始した件数（スキップ分を除く） */
    started: number;
    skipped: BulkTranscriptionSkipped;
    /** 1 回に実行できる件数の上限（BE 設定値） */
    limit: number;
    /** 実行対象のうち長さが分かっているぶんの合計（秒） */
    total_seconds?: number;
  };
}

/** 単体 / 一括のリクエストボディに載せる実行オプション */
const toRequestBody = (options: TranscriptionOptions) => ({
  language_code: options.languageCode ?? "auto",
  speaker_labels: options.speakerLabels ?? false,
  mode: options.mode ?? "conversation",
  ...(options.promptHint ? { prompt_hint: options.promptHint } : {}),
});

export const mediaTranscriptionApi = createApi({
  reducerPath: "mediaTranscriptionApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["Transcription"],
  endpoints: (builder) => ({
    getTranscription: builder.query<TranscriptionResponse, string>({
      query: (fileId) => `/api/media/${fileId}/transcription`,
      providesTags: (_result, _error, fileId) => [
        { type: "Transcription", id: fileId },
      ],
    }),

    startTranscription: builder.mutation<
      TranscriptionResponse,
      StartTranscriptionArgs
    >({
      query: ({ fileId, ...options }) => ({
        url: `/api/media/${fileId}/transcription`,
        method: "POST",
        body: toRequestBody(options),
      }),
      // 実行後は pending に変わるため、購読中のステータスを即時再取得させる
      invalidatesTags: (_result, _error, { fileId }) => [
        { type: "Transcription", id: fileId },
      ],
    }),

    deleteTranscription: builder.mutation<TranscriptionResponse, string>({
      query: (fileId) => ({
        url: `/api/media/${fileId}/transcription`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, fileId) => [
        { type: "Transcription", id: fileId },
      ],
    }),

    /**
     * 一覧ぶんのステータスを 1 リクエストで取得する（チップ表示用）。
     *
     * 副作用は無いが、fileIds が多いと URL 長の制限に当たるため POST。
     * `providesTags` に各 fileId を並べるので、単体の実行 / 削除で
     * `Transcription` タグが無効化されると一覧も自動で追従する。
     */
    getTranscriptionStatuses: builder.query<
      TranscriptionStatusesResponse,
      string[]
    >({
      query: (fileIds) => ({
        url: "/api/media/transcription/statuses",
        method: "POST",
        body: { file_ids: fileIds },
      }),
      providesTags: (_result, _error, fileIds) =>
        fileIds.map((fileId) => ({
          type: "Transcription" as const,
          id: fileId,
        })),
    }),

    startBulkTranscription: builder.mutation<
      BulkTranscriptionResponse,
      BulkTranscriptionArgs
    >({
      query: ({ fileIds, ...options }) => ({
        url: "/api/media/transcription/bulk",
        method: "POST",
        body: { file_ids: fileIds, ...toRequestBody(options) },
      }),
      invalidatesTags: (_result, _error, { fileIds }) =>
        fileIds.map((fileId) => ({
          type: "Transcription" as const,
          id: fileId,
        })),
    }),
  }),
});

export const {
  useGetTranscriptionQuery,
  useStartTranscriptionMutation,
  useDeleteTranscriptionMutation,
  useGetTranscriptionStatusesQuery,
  useStartBulkTranscriptionMutation,
} = mediaTranscriptionApi;
